import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { fmtStopwatch } from './clockUtils';
import { useTicker } from './useTicker';
import { RoundButton } from './RoundButton';
import { LapList } from './LapList';
import { useFloatingTimer } from '../../lib/useFloatingTimer';

export const StopwatchPanel = () => {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [laps, setLaps] = useState([]);
  const startRef = useRef(0);
  const baseRef = useRef(0);

  useTicker(running, useCallback((now) => setElapsed(baseRef.current + now - startRef.current), []));
  useFloatingTimer(running || elapsed > 0, { mode: 'stopwatch', label: 'Stopwatch', elapsedMs: elapsed, paused: !running, onPause: () => stop(), onResume: () => start() });

  const start = () => { startRef.current = Date.now(); baseRef.current = elapsed; setRunning(true); };
  const stop = () => setRunning(false);
  const reset = () => { setElapsed(0); setLaps([]); baseRef.current = 0; };
  const lap = () => setLaps((prev) => [elapsed, ...prev]);

  const { main, cs } = fmtStopwatch(elapsed);
  const idle = !running && elapsed === 0;

  return (
    <div className="flex flex-col items-center" data-testid="stopwatch-panel">
      <div className="relative py-10 sm:py-14 w-full flex justify-center">
        <div className="pointer-events-none absolute inset-0 m-auto h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
        <p className="relative tabular-nums font-bold text-white text-[40px] min-[380px]:text-[46px] sm:text-[60px] leading-none tracking-tight" data-testid="stopwatch-display">
          {main}<span className="text-white/60">.{cs}</span>
        </p>
      </div>

      <LapList laps={laps} current={elapsed} />

      <div className="mt-6 flex w-full max-w-[300px] items-center justify-between">
        <AnimatePresence mode="wait" initial={false}>
          {idle ? (
            <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mx-auto">
              <RoundButton variant="primary" onClick={start} data-testid="stopwatch-start-btn">Start</RoundButton>
            </motion.div>
          ) : (
            <motion.div key="active" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex w-full justify-between">
              {running
                ? <RoundButton onClick={lap} data-testid="stopwatch-lap-btn">Lap</RoundButton>
                : <RoundButton onClick={reset} data-testid="stopwatch-reset-btn">Reset</RoundButton>}
              {running
                ? <RoundButton variant="danger" onClick={stop} data-testid="stopwatch-stop-btn">Stop</RoundButton>
                : <RoundButton variant="primary" onClick={start} data-testid="stopwatch-resume-btn">Resume</RoundButton>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StopwatchPanel;
